import { ApiPlatformEnum, EcommerceCartDataSourceEnum, EcommerceShoppingListDataSourceEnum } from '@inter-ikea-kompis/enums';
import { ExceptionTypeEnum, ServiceException } from '@inter-ikea-kompis/exceptions';
import { GlobalCacheStore } from '@inter-ikea-kompis/utilities';
import ChinaAuthenticationService from '../../services/authentication/china/ChinaAuthenticationService.js';
import ChinaShoppingListService from '../../services/shopping/china/shopping-list/ChinaShoppingListService.js';
import IksaShoppingBagService from '../../services/shopping/iksa/IksaShoppingBagService.js';
import IngkaShoppingListService from '../../services/shopping/ingka/shopping-list/IngkaShoppingListService.js';
import IowsShoppingBagService from '../../services/shopping/iows/IowsShoppingBagService.js';
import MvEcomShoppingBagService from '../../services/shopping/mvecom/MvEcomShoppingBagService.js';
import DexfConfiguration from '../../utilities/DexfConfiguration.js';
import LocalizationService from '../localization/LocalizationService.js';
import AuthenticationQueue from './AuthenticationQueue.js';
/**
 * This class is used as a simple interface for shopping services.
 */
export default class ShoppingListService {
    /**
     * Constructor.
     *
     * @param options Options.
     */
    constructor(options) {
        this.consumer = options.consumer;
        this.contract = options.contract;
        this.localizationPrefixApplication = options.localizationPrefixApplication;
        this.localizationEndpoints = options.localizationEndpoints;
        this.localizationOverrides = options.localizationOverrides;
        this.locale = options.locale;
        this.shoppingListName = options.shoppingListName;
        this.cache = options.cache || new GlobalCacheStore();
        this.dexfConfiguration = new DexfConfiguration(options);
        this.translations = options.translations;
    }
    /**
     * Adds items to shopping cart in IRW.
     *
     * @param options Options.
     * @param options.items Array of shopping items.
     * @param options.designCode VPC code used to group articles in the shopping list.
     * @param options.singleItems Single items to be added individually outside the group. Only applicable when a designCode has been set.
     * @returns Promise object.
     */
    async addToList(options) {
        const { kompis } = await this.dexfConfiguration.getSettings();
        const eCommerceCartDataSource = kompis.serviceSettings.eCommerceCartDataSource;
        const eCommerceShoppingListDataSource = kompis.serviceSettings.eCommerceShoppingListDataSource;
        const apiPlatform = kompis.serviceSettings.apiPlatform;
        switch (eCommerceShoppingListDataSource) {
            case EcommerceShoppingListDataSourceEnum.shoppingListApi:
                return await this.addToListOneWeb({
                    items: options.items,
                    designCode: options.designCode,
                    singleItems: options.singleItems
                });
            case EcommerceShoppingListDataSourceEnum.chinaShoppingListApi:
                return await this.addToListChina({
                    items: options.items
                });
            case EcommerceShoppingListDataSourceEnum.iows:
                return await this.addToListIows({
                    items: options.items
                });
            case EcommerceShoppingListDataSourceEnum.mvecom:
                return await this.addToListMvEcom({
                    items: options.items
                });
            case EcommerceShoppingListDataSourceEnum.iksa:
                return await this.addToListIksa({
                    items: options.items
                });
            case EcommerceShoppingListDataSourceEnum.default:
                switch (eCommerceCartDataSource) {
                    case EcommerceCartDataSourceEnum.iows:
                    case EcommerceCartDataSourceEnum.cartApi:
                        return await this.addToListIows({
                            items: options.items
                        });
                    case EcommerceCartDataSourceEnum.default:
                        switch (apiPlatform) {
                            case ApiPlatformEnum.irw:
                                return await this.addToListIows({
                                    items: options.items
                                });
                            default:
                                throw new ServiceException(`Failed to add items to shopping list. Unknown api platform "${apiPlatform}".`);
                        }
                    default:
                        throw new ServiceException(`Failed to add items to shopping list. Unknown shopping cart data source "${eCommerceCartDataSource}".`);
                }
            default:
                throw new ServiceException(`Failed to add items to shopping list. Unknown shopping list data source "${eCommerceShoppingListDataSource}".`);
        }
    }
    /**
     * Returns total amount of items in a shopping list.
     *
     * @returns Promise object.
     */
    async getListCount() {
        const { kompis } = await this.dexfConfiguration.getSettings();
        const eCommerceCartDataSource = kompis.serviceSettings.eCommerceCartDataSource;
        const eCommerceShoppingListDataSource = kompis.serviceSettings.eCommerceShoppingListDataSource;
        const apiPlatform = kompis.serviceSettings.apiPlatform;
        switch (eCommerceShoppingListDataSource) {
            case EcommerceShoppingListDataSourceEnum.shoppingListApi:
                return await this.getListCountOneWeb();
            case EcommerceShoppingListDataSourceEnum.iows:
                return await this.getListCountIows();
            case EcommerceShoppingListDataSourceEnum.mvecom:
                return await this.getListCountMvEcom();
            case EcommerceShoppingListDataSourceEnum.chinaShoppingListApi:
                return await this.getListCountChina();
            case EcommerceShoppingListDataSourceEnum.iksa:
                return await this.getListCountIksa();
            case EcommerceShoppingListDataSourceEnum.default:
                switch (eCommerceCartDataSource) {
                    case EcommerceCartDataSourceEnum.iows:
                    case EcommerceCartDataSourceEnum.cartApi:
                        return await this.getListCountIows();
                    case EcommerceCartDataSourceEnum.default:
                        switch (apiPlatform) {
                            case ApiPlatformEnum.irw:
                                return await this.getListCountIows();
                            default:
                                throw new ServiceException(`Failed to get list count. Unknown api platform "${apiPlatform}".`);
                        }
                    default:
                        throw new ServiceException(`Failed to get list count. Unknown shopping cart data source "${eCommerceCartDataSource}".`);
                }
            default:
                throw new ServiceException(`Failed to get list count. Unknown shopping list data source "${eCommerceShoppingListDataSource}".`);
        }
    }
    /**
     * Returns shopping list name.
     */
    async getShoppingListName() {
        if (this.shoppingListName) {
            return this.shoppingListName;
        }
        else if (this.translations) {
            return this.translations.plannerShoppingListName;
        }
        else if (this.localizationEndpoints && this.locale) {
            const localizationService = new LocalizationService({
                localizationPrefixApplication: this.localizationPrefixApplication,
                localizationEndpoints: this.localizationEndpoints,
                localizationOverrides: this.localizationOverrides,
                locale: this.locale,
                cache: this.cache
            });
            const localizedInformation = await localizationService.getLocalizedInformation();
            return localizedInformation.translations.plannerShoppingListName;
        }
        throw new Error('Shopping list name must be defined either through the "shoppingListName", "translations" option or "localizedInformation" props.');
    }
    /**
     * Adds items to shopping cart.
     *
     * @param options Options.
     * @param options.items Array of shopping items.
     * @returns Promise object.
     */
    async addToListIows(options) {
        const { kompis } = await this.dexfConfiguration.getSettings();
        const shoppingService = new IowsShoppingBagService({
            consumer: this.consumer,
            contract: this.contract,
            endpoint: kompis.endpoints.iowsShoppingBagService
        });
        const authQueue = new AuthenticationQueue(this.cache);
        const user = await authQueue.getAuthenticationIows({
            consumer: this.consumer,
            contract: this.contract,
            endpoint: kompis.endpoints.iowsAuthenticationServiceV2
        });
        const shoppingList = await shoppingService.addToList({
            items: options.items,
            userId: user.id,
            shoppingListName: await this.getShoppingListName()
        });
        return shoppingList;
    }
    /**
     * Returns list count for IOWS.
     *
     * @returns Promise object.
     */
    async getListCountIows() {
        const { kompis } = await this.dexfConfiguration.getSettings();
        const shoppingService = new IowsShoppingBagService({
            consumer: this.consumer,
            contract: this.contract,
            endpoint: kompis.endpoints.iowsShoppingBagService
        });
        const authQueue = new AuthenticationQueue(this.cache);
        const user = await authQueue.getAuthenticationIows({
            consumer: this.consumer,
            contract: this.contract,
            endpoint: kompis.endpoints.iowsAuthenticationServiceV2
        });
        return await shoppingService.getListCount({
            userId: user.id,
            shoppingListName: await this.getShoppingListName()
        });
    }
    /**
     * Adds items to shopping list for MvEcom.
     *
     * @param options Options.
     * @param options.items Array of shopping items.
     * @returns Promise object.
     */
    async addToListMvEcom(options) {
        const { kompis } = await this.dexfConfiguration.getSettings();
        const shoppingService = new MvEcomShoppingBagService({
            mvEcomApiKey: kompis.serviceSettings.mvEcomApiKey,
            endpoint: kompis.endpoints.mvEcomShoppingBagService
        });
        const authQueue = new AuthenticationQueue(this.cache);
        const user = await authQueue.getAuthenticationIows({
            consumer: this.consumer,
            contract: this.contract,
            endpoint: kompis.endpoints.iowsAuthenticationServiceV2
        });
        const shoppingList = await shoppingService.addToList({
            items: options.items,
            userId: user.id,
            shoppingListName: await this.getShoppingListName()
        });
        return shoppingList;
    }
    /**
     * Returns list count for MvEcom.
     *
     * @returns Promise object.
     */
    async getListCountMvEcom() {
        const { kompis } = await this.dexfConfiguration.getSettings();
        const shoppingService = new MvEcomShoppingBagService({
            mvEcomApiKey: kompis.serviceSettings.mvEcomApiKey,
            endpoint: kompis.endpoints.mvEcomShoppingBagService
        });
        const authQueue = new AuthenticationQueue(this.cache);
        const user = await authQueue.getAuthenticationIows({
            consumer: this.consumer,
            contract: this.contract,
            endpoint: kompis.endpoints.iowsAuthenticationServiceV2
        });
        return await shoppingService.getListCount({
            userId: user.id,
            shoppingListName: await this.getShoppingListName()
        });
    }
    /**
     * Adds items to shopping list.
     *
     * @param options Options.
     * @param options.items Array of shopping items.
     * @param options.designCode ...
     * @param options.singleItems ...
     * @returns Promise object.
     */
    async addToListOneWeb(options) {
        const { kompis } = await this.dexfConfiguration.getSettings();
        const shoppingService = new IngkaShoppingListService({
            endpoint: kompis.endpoints.ingkaShoppingListService,
            clientId: kompis.serviceSettings.ingkaShoppingListClientId,
            clientSecret: kompis.serviceSettings.ingkaShoppingListClientSecret,
            itemGroupingInBagEnabled: kompis.serviceSettings.itemGroupingInBagEnabled
        });
        const authQueue = new AuthenticationQueue(this.cache);
        const token = await authQueue.getAuthTokenOneWeb({
            endpoint: kompis.endpoints.oneWebAuthenticationToken
        });
        if (!token) {
            throw new ServiceException('Token is not valid.');
        }
        const shoppingList = await shoppingService.addToList({
            items: options.items,
            token,
            shoppingListName: await this.getShoppingListName(),
            designCode: options.designCode,
            singleItems: options.singleItems
        });
        return shoppingList;
    }
    /**
     * Returns list count for One Web.
     *
     * @returns Promise object.
     */
    async getListCountOneWeb() {
        const { kompis } = await this.dexfConfiguration.getSettings();
        const shoppingService = new IngkaShoppingListService({
            endpoint: kompis.endpoints.ingkaShoppingListService,
            clientId: kompis.serviceSettings.ingkaShoppingListClientId,
            clientSecret: kompis.serviceSettings.ingkaShoppingListClientSecret,
            itemGroupingInBagEnabled: kompis.serviceSettings.itemGroupingInBagEnabled
        });
        const authQueue = new AuthenticationQueue(this.cache);
        const token = await authQueue.getAuthTokenOneWeb({
            endpoint: kompis.endpoints.oneWebAuthenticationToken
        });
        if (!token) {
            throw new ServiceException('Token is not valid.');
        }
        return await shoppingService.getListCount({
            token,
            shoppingListName: await this.getShoppingListName()
        });
    }
    /**
     * Adds items to shopping list.
     *
     * @param options Options.
     * @param options.items Array of shopping items.
     * @returns Promise object.
     */
    async addToListChina(options) {
        const { kompis } = await this.dexfConfiguration.getSettings();
        const shoppingService = new ChinaShoppingListService({
            endpoint: kompis.endpoints.chinaShoppingListService
        });
        const authQueue = new AuthenticationQueue(this.cache);
        const token = await authQueue.getAuthTokenChina({
            refreshTokenEndpoint: kompis.endpoints.chinaAuthenticationRefreshToken,
            loginPageEndpoint: kompis.endpoints.chinaAuthenticationLoginPage
        });
        if (!token) {
            throw new ServiceException('Token is not valid.');
        }
        try {
            return await shoppingService.addToList({
                items: options.items,
                token
            });
        }
        catch (error) {
            if (error.exceptionType === ExceptionTypeEnum.serverErrorResponse &&
                error.serverResponse.status === 401) {
                const token = await authQueue.getAuthTokenChina({
                    refreshTokenEndpoint: kompis.endpoints.chinaAuthenticationRefreshToken,
                    loginPageEndpoint: kompis.endpoints.chinaAuthenticationLoginPage,
                    login: true
                });
                if (!token) {
                    throw new ServiceException('Token is not valid.');
                }
                return await shoppingService.addToList({
                    items: options.items,
                    token
                });
            }
            throw error;
        }
    }
    /**
     * Returns list count for China.
     *
     * @returns Promise object.
     */
    async getListCountChina() {
        const { kompis } = await this.dexfConfiguration.getSettings();
        if (!new ChinaAuthenticationService({
            refreshTokenEndpoint: kompis.endpoints.chinaAuthenticationRefreshToken,
            loginPageEndpoint: kompis.endpoints.chinaAuthenticationLoginPage
        }).hasCookie()) {
            return 0;
        }
        const shoppingService = new ChinaShoppingListService({
            endpoint: kompis.endpoints.chinaShoppingListService
        });
        const authQueue = new AuthenticationQueue(this.cache);
        const token = await authQueue.getAuthTokenChina({
            refreshTokenEndpoint: kompis.endpoints.chinaAuthenticationRefreshToken,
            loginPageEndpoint: kompis.endpoints.chinaAuthenticationLoginPage
        });
        if (!token) {
            throw new ServiceException('Token is not valid.');
        }
        try {
            return await shoppingService.getListCount({
                token
            });
        }
        catch (error) {
            if (error.exceptionType === ExceptionTypeEnum.serverErrorResponse &&
                error.serverResponse.status === 401) {
                const token = await authQueue.getAuthTokenChina({
                    refreshTokenEndpoint: kompis.endpoints.chinaAuthenticationRefreshToken,
                    loginPageEndpoint: kompis.endpoints.chinaAuthenticationLoginPage,
                    login: true
                });
                if (!token) {
                    throw new ServiceException('Token is not valid.');
                }
                return await shoppingService.getListCount({
                    token
                });
            }
            throw error;
        }
    }
    /**
     * Adds items to shopping cart.
     *
     * @param options Options.
     * @param options.items Array of shopping items.
     * @returns Promise object.
     */
    async addToListIksa(options) {
        const { kompis } = await this.dexfConfiguration.getSettings();
        const shoppingService = new IksaShoppingBagService({
            consumer: this.consumer,
            contract: this.contract,
            endpoint: kompis.endpoints.iksaShoppingBagService
        });
        const authQueue = new AuthenticationQueue(this.cache);
        const user = await authQueue.getAuthenticationIows({
            consumer: this.consumer,
            contract: this.contract,
            endpoint: kompis.endpoints.iowsAuthenticationServiceV2
        });
        const shoppingList = await shoppingService.addToList({
            items: options.items,
            userId: user.id,
            shoppingListName: await this.getShoppingListName()
        });
        return shoppingList;
    }
    /**
     * Returns list count for IKSA.
     *
     * @returns Promise object.
     */
    async getListCountIksa() {
        const { kompis } = await this.dexfConfiguration.getSettings();
        const shoppingService = new IksaShoppingBagService({
            consumer: this.consumer,
            contract: this.contract,
            endpoint: kompis.endpoints.iksaShoppingBagService
        });
        const authQueue = new AuthenticationQueue(this.cache);
        const user = await authQueue.getAuthenticationIows({
            consumer: this.consumer,
            contract: this.contract,
            endpoint: kompis.endpoints.iowsAuthenticationServiceV2
        });
        return await shoppingService.getListCount({
            userId: user.id,
            shoppingListName: await this.getShoppingListName()
        });
    }
}
//# sourceMappingURL=ShoppingListService.js.map